import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Navbar.css';
import { assets } from '../../assets/assets';
import LoginPopup from '../LoginPopup/LoginPopup';
import Cart from '../Cart/Cart';
import SearchDropdown from '../SearchDropdown/SearchDropdown';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useNav } from '../../Context/NavContext';

const Navbar = () => {
  const [menu, setMenu] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showCartPopup, setShowCartPopup] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null); // Default fallback
  const navigate = useNavigate();

  // Base URL for API calls
  const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";

  // Clerk authentication hooks
  const { isSignedIn, user, isLoaded } = useUser();
  const { signOut } = useClerk();

  // Custom context hooks
  const { isAuthenticated, userInfo, login, logout } = useNav();

  // Fetch contact information including phone number
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/contact/get`);
        const data = response.data.data;
        
        if (data) {
          setContactInfo(data);
          // Update phone number if available, otherwise keep default
          if (data.phoneNumber && data.phoneNumber.trim() !== '') {
            setPhoneNumber(data.phoneNumber);
          }
        }
      } catch (error) {
        // Silently fail and use default phone number
        console.log('Contact info not available, using default phone number');
      }
    };

    fetchContactInfo();
  }, [baseURL]);

  // Change navbar color on scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const userProfileContainer = document.querySelector('.nav-user-profile');
      const searchContainer = document.querySelector('.nav-search-container');
      const mobileMenu = document.querySelector('.nav-menu');
      const mobileMenuToggle = document.querySelector('.nav-mobile-toggle');

      if (showDropdown && userProfileContainer && !userProfileContainer.contains(event.target)) {
        setShowDropdown(false);
      }

      if (showSearchDropdown && searchContainer && !searchContainer.contains(event.target)) {
        setShowSearchDropdown(false);
      }

      // Close mobile menu when clicking outside
      if (mobileMenuOpen && mobileMenu && !mobileMenu.contains(event.target) && 
          mobileMenuToggle && !mobileMenuToggle.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, showSearchDropdown, mobileMenuOpen]);

  // Handle window resize to close mobile menu on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Sync Clerk authentication with NavContext
  useEffect(() => {
    if (isLoaded) {
      if (isSignedIn && user && !isAuthenticated) {
        // User is signed in with Clerk but not in context - sync them
        const firstName = user.firstName || '';
        const lastName = user.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();

        const userData = {
          name: fullName || user.primaryEmailAddress?.emailAddress || 'User',
          email: user.primaryEmailAddress?.emailAddress || '',
          isLoggedIn: true,
          profileImage: user.imageUrl,
          provider: user.externalAccounts.length > 0
            ? user.externalAccounts[0].provider.toUpperCase()
            : 'EMAIL',
        };

        login(userData);
      }

      // Close login popup if user becomes authenticated
      if ((isAuthenticated || isSignedIn) && showLoginPopup) {
        setShowLoginPopup(false);
      }
    }
  }, [isLoaded, isSignedIn, user, isAuthenticated, login, showLoginPopup]);

  // Function to handle menu clicks and navigate
  const handleMenuClick = (menuName, path) => {
    setMenu(menuName);
    setMobileMenuOpen(false); // Close mobile menu when navigating
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(path);
  };

  // Function to toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    // Close other dropdowns when opening mobile menu
    if (!mobileMenuOpen) {
      setShowDropdown(false);
      setShowSearchDropdown(false);
    }
  };

  // Function to toggle dropdown
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
    // Close mobile menu if open
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  // Function to toggle search dropdown
  const toggleSearchDropdown = () => {
    setShowSearchDropdown(!showSearchDropdown);
    // Close mobile menu if open
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  // Function to handle phone call
  const handlePhoneCall = () => {
    window.location.href = `tel:${phoneNumber}`;
  };

  // Function to handle user profile click
  const handleUserProfileClick = () => {
    setShowDropdown(false);
    setMobileMenuOpen(false);
    navigate('/userprofile');
  };

  // Function to handle logout
  const handleLogout = async () => {
    try {
      // Use the context logout function which handles both localStorage and Clerk
      await logout();
      setShowDropdown(false);
      setMobileMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: force logout even if there's an error
      setShowDropdown(false);
      setMobileMenuOpen(false);
      navigate('/');
    }
  };

  // Function to handle successful login/registration
  const handleLoginSuccess = (userData) => {
    setShowLoginPopup(false);
    setShowDropdown(false);
    setMobileMenuOpen(false);

    // Navigate to user profile after successful authentication
    navigate('/userprofile');
  };

  // Function to handle login button click
  const handleLoginClick = () => {
    setShowLoginPopup(true);
    setMobileMenuOpen(false);
  };

  // Function to handle cart click
  const handleCartClick = () => {
    setShowCartPopup(true);
    setMobileMenuOpen(false);
  };

  // Get user initials for the profile icon
  const getUserInitials = () => {
    // First try to get from userInfo (NavContext)
    if (isAuthenticated && userInfo?.name) {
      const name = userInfo.name.trim();
      const nameParts = name.split(' ');

      if (nameParts.length >= 2) {
        return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
      } else if (nameParts.length === 1) {
        return nameParts[0].charAt(0).toUpperCase();
      }
    }

    // Fallback to Clerk user data
    if (isLoaded && isSignedIn && user) {
      const firstName = user.firstName || '';
      const lastName = user.lastName || '';

      if (firstName && lastName) {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
      } else if (firstName) {
        return firstName.charAt(0).toUpperCase();
      } else if (user.primaryEmailAddress) {
        return user.primaryEmailAddress.emailAddress.charAt(0).toUpperCase();
      }
    }

    // Default fallback
    return 'U';
  };

  // Get user profile image
  const getUserProfileImage = () => {
    // First try userInfo from context
    if (isAuthenticated && userInfo?.profileImage) {
      return userInfo.profileImage;
    }

    // Fallback to Clerk user image
    if (isLoaded && isSignedIn && user?.imageUrl) {
      return user.imageUrl;
    }

    return null;
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (isAuthenticated && userInfo?.name) {
      return userInfo.name;
    }

    if (isLoaded && isSignedIn && user) {
      const firstName = user.firstName || '';
      const lastName = user.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName || user.primaryEmailAddress?.emailAddress || 'User';
    }

    return 'User';
  };

  // Determine if user is authenticated (either through context or Clerk)
  const isUserAuthenticated = isAuthenticated || (isLoaded && isSignedIn);

  // Handle keyboard navigation for accessibility
  const handleKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  return (
    <>
      <div className={`nav-main ${scrolled ? 'nav-scrolled' : ''}`}>
        <img 
          src={assets.logo_new} 
          alt='Logo' 
          className='nav-logo' 
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            navigate('/');
          }}
          onKeyDown={(e) => handleKeyDown(e, () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            navigate('/');
          })}
          tabIndex="0"
          role="button"
          aria-label="Go to homepage"
        />

        {/* Mobile Menu Toggle */}
        <div 
          className={`nav-mobile-toggle ${mobileMenuOpen ? 'nav-active' : ''}`}
          onClick={toggleMobileMenu}
          onKeyDown={(e) => handleKeyDown(e, toggleMobileMenu)}
          tabIndex="0"
          role="button"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>

        <ul className={`nav-menu ${mobileMenuOpen ? 'nav-active' : ''}`}>
          <li 
            onClick={() => handleMenuClick('home', '/')}
            onKeyDown={(e) => handleKeyDown(e, () => handleMenuClick('home', '/'))}
            tabIndex="0"
            role="button"
            className={menu === 'home' ? 'nav-active' : ''}
          >
            Home
          </li>
          <li 
            onClick={() => handleMenuClick('about', '/about')}
            onKeyDown={(e) => handleKeyDown(e, () => handleMenuClick('about', '/about'))}
            tabIndex="0"
            role="button"
            className={menu === 'about' ? 'nav-active' : ''}
          >
            About
          </li>
          <li 
            onClick={() => handleMenuClick('service', '/service')}
            onKeyDown={(e) => handleKeyDown(e, () => handleMenuClick('service', '/service'))}
            tabIndex="0"
            role="button"
            className={menu === 'service' ? 'nav-active' : ''}
          >
            Service
          </li>
          <li 
            onClick={() => handleMenuClick('contact', '/contact')}
            onKeyDown={(e) => handleKeyDown(e, () => handleMenuClick('contact', '/contact'))}
            tabIndex="0"
            role="button"
            className={menu === 'contact' ? 'nav-active' : ''}
          >
            Contact
          </li>
        </ul>

        <div className='nav-right'>
          {/* Dynamic Phone Number from Backend */}
          <div 
            className='nav-phone-container'
            onClick={handlePhoneCall}
            onKeyDown={(e) => handleKeyDown(e, handlePhoneCall)}
            tabIndex="0"
            role="button"
            aria-label={`Call us at ${phoneNumber}`}
            title={`Call us at ${phoneNumber}`}
          >
            <div className='nav-phone-icon'>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
            </div>
            <span className='nav-phone-number'>{phoneNumber}</span>
          </div>

          <div className='nav-search-container'>
            <img
              src={assets.search_icon}
              alt='Search Icon'
              className='nav-search-icon'
              onClick={toggleSearchDropdown}
              onKeyDown={(e) => handleKeyDown(e, toggleSearchDropdown)}
              tabIndex="0"
              role="button"
              aria-label="Toggle search"
              aria-expanded={showSearchDropdown}
            />
            {showSearchDropdown && (
              <SearchDropdown onClose={() => setShowSearchDropdown(false)} />
            )}
          </div>

          <div 
            className='nav-cart-icon' 
            onClick={handleCartClick}
            onKeyDown={(e) => handleKeyDown(e, handleCartClick)}
            tabIndex="0"
            role="button"
            aria-label="Open cart"
          >
            <Cart />
          </div>

          {/* User authentication section commented out in original */}
          {/*
          {isUserAuthenticated ? (
            <div className='nav-user-profile'>
              <div 
                className='nav-user-icon' 
                onClick={toggleDropdown}
                onKeyDown={(e) => handleKeyDown(e, toggleDropdown)}
                tabIndex="0"
                role="button"
                title={getUserDisplayName()}
                aria-label={`User menu for ${getUserDisplayName()}`}
                aria-expanded={showDropdown}
              >
                {getUserProfileImage() ? (
                  <img 
                    src={getUserProfileImage()} 
                    alt='Profile' 
                    className='nav-user-avatar' 
                  />
                ) : (
                  <span className='nav-user-initials'>{getUserInitials()}</span>
                )}
              </div>
              {showDropdown && (
                <div className='nav-user-dropdown' role="menu">
                  <div className='nav-user-info'>
                    <div className='nav-user-name'>{getUserDisplayName()}</div>
                    <div className='nav-user-email'>
                      {userInfo?.email || user?.primaryEmailAddress?.emailAddress || ''}
                    </div>
                  </div>
                  <div className='nav-dropdown-divider'></div>
                  <div 
                    className='nav-dropdown-item' 
                    onClick={handleUserProfileClick}
                    onKeyDown={(e) => handleKeyDown(e, handleUserProfileClick)}
                    tabIndex="0"
                    role="menuitem"
                  >
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' />
                    </svg>
                    My Profile
                  </div>
                  <div 
                    className='nav-dropdown-item' 
                    onClick={() => {
                      setShowDropdown(false);
                      setMobileMenuOpen(false);
                      navigate('/orders');
                    }}
                    onKeyDown={(e) => handleKeyDown(e, () => {
                      setShowDropdown(false);
                      setMobileMenuOpen(false);
                      navigate('/orders');
                    })}
                    tabIndex="0"
                    role="menuitem"
                  >
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M19 7h-1V6a4 4 0 0 0-8 0v1H9a1 1 0 0 0-1 1v11a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3V8a1 1 0 0 0-1-1zM10.5 6a1.5 1.5 0 0 1 3 0v1h-3V6zM18 19a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1V9h2v1a1 1 0 0 0 2 0V9h2v1a1 1 0 0 0 2 0V9h2v10z' />
                    </svg>
                    My Orders
                  </div>
                  <div 
                    className='nav-dropdown-item nav-logout-item' 
                    onClick={handleLogout}
                    onKeyDown={(e) => handleKeyDown(e, handleLogout)}
                    tabIndex="0"
                    role="menuitem"
                  >
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z' />
                    </svg>
                    Logout
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button 
              className='nav-login-btn' 
              onClick={handleLoginClick}
              onKeyDown={(e) => handleKeyDown(e, handleLoginClick)}
              aria-label="Login to your account"
            >
              Login
            </button>
          )}
          */}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="nav-mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Popups */}
      {showLoginPopup && (
        <LoginPopup 
          onClose={() => setShowLoginPopup(false)} 
          onLoginSuccess={handleLoginSuccess} 
        />
      )}
      {showCartPopup && (
        <Cart onClose={() => setShowCartPopup(false)} />
      )}
    </>
  );
};

export default Navbar;